import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ErrorAlert, Loading } from '../components/ui';

/**
 * กรอก LINE channel ของมูลนิธินี้
 *
 * ค่าความลับเป็น **เขียนอย่างเดียว** — API ไม่เคยคืน secret หรือ access token
 * กลับมา หน้านี้จึงบอกได้แค่ "กรอกแล้วหรือยัง" และช่องกรอกจะว่างเสมอ
 * เว้นว่างไว้ = ไม่แตะของเดิม
 */

interface ChannelState {
  channelId: string | null;
  hasSecret: boolean;
  hasAccessToken: boolean;
  liffId: string | null;
}

interface ChannelsResponse {
  orgSlug: string;
  channels: Record<string, ChannelState>;
  webhookUrls: { staff: string; public: string };
}

const KINDS = [
  {
    key: 'staff_login',
    field: 'staffLogin',
    title: 'LINE Login — LIFF เจ้าหน้าที่',
    hint: 'ใช้ยืนยันตัวตนเจ้าหน้าที่ตอนเปิดแอปจากเมนู',
    needsToken: false,
    needsLiff: true,
  },
  {
    key: 'staff_messaging',
    field: 'staffMessaging',
    title: 'Messaging API — OA เจ้าหน้าที่',
    hint: 'ใช้เตือนงานลงพื้นที่และส่งสรุปประจำวัน',
    needsToken: true,
    needsLiff: false,
  },
  {
    key: 'public_login',
    field: 'publicLogin',
    title: 'LINE Login — LIFF ผู้บริจาค/ประชาชน',
    hint: 'ใช้ยืนยันตัวตนผู้บริจาคและคนที่ยื่นขอความช่วยเหลือ',
    needsToken: false,
    needsLiff: true,
  },
  {
    key: 'public_messaging',
    field: 'publicMessaging',
    title: 'Messaging API — OA สาธารณะ',
    hint: 'ใช้รับสลิปที่ส่งเข้าแชท และส่งข้อความขอบคุณ',
    needsToken: true,
    needsLiff: false,
  },
] as const;

export default function LineChannels() {
  const [state, setState] = useState<ChannelsResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setState(await api<ChannelsResponse>('/settings/line/channels'));
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(kind: string, form: HTMLFormElement) {
    const data = new FormData(form);
    const channelId = String(data.get('channelId') ?? '').trim();
    if (!channelId) {
      setError(new Error('ต้องกรอก Channel ID'));
      return;
    }

    // ส่งเฉพาะช่องที่กรอกมาจริง — ช่องว่างแปลว่า "ไม่แตะของเดิม" ไม่ใช่ "ลบทิ้ง"
    const body: Record<string, string> = { channelId };
    for (const f of ['secret', 'accessToken', 'liffId'] as const) {
      const v = String(data.get(f) ?? '').trim();
      if (v) body[f] = v;
    }

    setBusy(kind);
    setError(null);
    setFlash(null);
    try {
      await api(`/settings/line/channels/${kind}`, { method: 'PUT', body });
      setFlash('บันทึกแล้ว');
      form.reset();
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>ช่องทาง LINE</h1>
          <p>ค่าจาก LINE Developers Console ของมูลนิธินี้</p>
        </div>
      </div>

      <ErrorAlert error={error} />
      {flash && <div className="alert success">{flash}</div>}

      {!state ? (
        <Loading />
      ) : (
        <>
          <div className="alert info">
            <strong>ตั้ง Webhook URL ใน LINE Console ให้ตรงกับสองอันนี้</strong>
            <ul style={{ paddingLeft: 20, marginTop: 6 }}>
              <li>
                OA เจ้าหน้าที่ <span className="mono">{state.webhookUrls.staff}</span>
              </li>
              <li>
                OA สาธารณะ <span className="mono">{state.webhookUrls.public}</span>
              </li>
            </ul>
            และตั้ง <strong>LIFF Endpoint URL</strong> ให้ลงท้ายด้วย{' '}
            <span className="mono">/o/{state.orgSlug}/</span> — แอปอ่านรหัสมูลนิธิจากตรงนั้น
          </div>

          {KINDS.map((k) => {
            const cur = state.channels[k.field];
            return (
              <form
                className="card"
                key={k.key}
                onSubmit={(e) => {
                  e.preventDefault();
                  void save(k.key, e.currentTarget);
                }}
              >
                <h2>
                  {k.title}{' '}
                  {cur?.channelId ? (
                    <span className="badge green">ตั้งแล้ว</span>
                  ) : (
                    <span className="badge amber">ยังไม่ได้ตั้ง</span>
                  )}
                </h2>
                <p className="hint">{k.hint}</p>

                <label>
                  Channel ID
                  <input name="channelId" defaultValue={cur?.channelId ?? ''} inputMode="numeric" />
                </label>

                <label>
                  Channel secret{' '}
                  <span className="hint">
                    {cur?.hasSecret ? '(ตั้งไว้แล้ว — เว้นว่างถ้าไม่เปลี่ยน)' : '(ยังไม่ได้ตั้ง)'}
                  </span>
                  <input name="secret" type="password" autoComplete="new-password" />
                </label>

                {k.needsToken && (
                  <label>
                    Channel access token{' '}
                    <span className="hint">
                      {cur?.hasAccessToken
                        ? '(ตั้งไว้แล้ว — เว้นว่างถ้าไม่เปลี่ยน)'
                        : '(ยังไม่ได้ตั้ง)'}
                    </span>
                    <input name="accessToken" type="password" autoComplete="new-password" />
                  </label>
                )}

                {k.needsLiff && (
                  <label>
                    LIFF ID <span className="hint">เช่น 2010925486-GIVyloMk</span>
                    <input name="liffId" defaultValue={cur?.liffId ?? ''} />
                  </label>
                )}

                <div className="btn-row">
                  <button className="btn" disabled={busy !== null}>
                    {busy === k.key ? 'กำลังบันทึก…' : 'บันทึก'}
                  </button>
                </div>
              </form>
            );
          })}

          <div className="alert warn">
            ระบบไม่เคยแสดง secret หรือ access token ที่บันทึกไว้แล้วกลับมา —
            ถ้าจำไม่ได้ให้ออกใหม่ที่ LINE Console แล้วกรอกทับ
          </div>
        </>
      )}
    </>
  );
}
